import { useEffect, useState } from "react";
import Flex from "../Flex/Flex";
import Button from "../forms/Button/Button";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { TeamMember, useUserContext } from "@/contexts/UserContext";
import { AssistantPermissions, ErrorCode, UserRole } from "@boxem/core-api-schema";
import { inviteMember, updateMember } from "@/api/teams";
import Checkbox from "../forms/Checkbox/Checkbox";
import TextInput from "../forms/TextInput/TextInput";
import Divider from "../Divider/Divider";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "../Modal/Modal";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";
import { FormField, useForm } from "../Form";
import { Grid } from "../Grid/Grid";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { logRocketError } from "@/utils/logUtils";


export interface MemberInviteModalModeProps {
  member?: TeamMember; // when set, this modal functions as an 'update' modal for the member.
  open: boolean;
  onClose: () => void;
  team: TeamMember[],
  setTeam: (memebers: TeamMember[]) => void;
}

export default function MemberInviteModal(props: MemberInviteModalModeProps) {
  const { member, open, onClose, team, setTeam } = props;

  const isUpdating = !!member;

  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { user, seller } = useUserContext();
  const { showNotification } = useNotificationContext();

  const [loading, setLoading] = useState<boolean>(false);
  const [topSectionVisible, setTopSectionVisible] = useState<boolean>(true);
  
  const form = useForm<TeamMember>({
    name: props.member ? props.member.name : "",
    email: props.member ? props.member.email : "",
    permissions: 0,
  });

  const handleSave = () => {
    if (!form.validate()) return;

    setLoading(true);

    if (member) {
      // update member
      updateMember({ ...member, ...form.value }).then(() => {
        showNotification("Member permissions updated successfully!", NotificationType.SUCCESS);
        onClose();
      }).catch((err) => {
        console.log(err);
        showNotification("Member permission update failed. Please try again later.", NotificationType.ERROR);
      }).finally(() => {
        setLoading(false);
      });
    } else {
      // invite new member
      inviteMember(seller.uuid, form.value).then(response => {
        const teamMembers = team ? [...team, {...form.value, ...response}] : [{...form.value, ...response}];
        setTeam(teamMembers);
        showNotification("Member invited successfully!", NotificationType.SUCCESS);
        onClose();
      }).catch((err) => {
        if (handleErrorResponseGenerically(err)) {
          showNotification("Member invitation failed! Please try again later or contact support.", NotificationType.ERROR);
          logRocketError(`Failed to invite assistant: ${err}, ${JSON.stringify({...member, ...form.value})}`);
          return;
        }
        switch (err[0]?.type) {
          case ErrorCode.SERVER_QUOTA_EXCEEDED: {
            const msg = "You have reached the maximum number of users you can invite.";
            showNotification(msg, NotificationType.ERROR);
          } break;
        }
      }).finally(() => {
        setLoading(false);
      })
    }
  };

  const setPermissionsFromBoolean = (bit: number, checked: boolean) => {
    if (checked) {
      form.setFieldValue("permissions", form.value.permissions | bit);
    } else {
      form.setFieldValue("permissions", form.value.permissions & ~bit);
    }
  }

  return (
    <Modal
      inlineWidth="min(50rem, 50vw)"
      open={open}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <ModalHeader>
        <h3>{isUpdating ? "Update Permissions" : "Invite User"}</h3>
      </ModalHeader>
      
      <ModalBody>
        <Flex direction="column" justify="space-between" align="center">
          {topSectionVisible && (
            <FormField
              label="Name:"
              name={"name"}
              form={form} 
            >
              <TextInput
                placeholder="enter the full name of your assistant"
                value={form.value.name}
                onChange={e => form.setFieldValue("name", e)}
                maxLength={255}
              />
            </FormField>
          )}

          {topSectionVisible && (
            <FormField
              label="Email:"
              validator={v => {
                if (v === user.email) {
                  return "You cannot invite yourself as an assistant!";
                }
                if (v === seller.email) {
                  return "You cannot invite the owner of your team to be an assistant on that team!";
                }
                if (!/^[^.][^@]+@[^@]+[^.]$/.test(v)) { // @TODO(nick) this should probably be the standard email regex we use (SES doesn't like leading or trailing dots)
                  return "Email is invalid";
                }
              }}
              name={"email"}
              form={form} 
            >
              <TextInput
                placeholder="enter the email of your assistant"
                value={form.value.email}
                onChange={e => form.setFieldValue("email", e)}
                maxLength={255}
              />
            </FormField>
          )}

          <Divider />

          <FormField
            form={form}
            label="Permissions:"
            validator={v => {
              if (v === 0) {
                return "You must grant the assistant at least one permission"
              }
            }}
            name="permissions"
          >
            <Grid templateColumns="1fr 1fr" gap="0.5rem">
              <label>
                <Checkbox
                  value={(form.value.permissions & AssistantPermissions.DASHBOARD_PERMISSION) !== 0}
                  onChange={(e) => setPermissionsFromBoolean(AssistantPermissions.DASHBOARD_PERMISSION, e)}
                />
                Dashboard
              </label>

              <label>
                <Checkbox
                  value={(form.value.permissions & AssistantPermissions.INVENTORY_PERMISSION) !== 0}
                  onChange={(e) => setPermissionsFromBoolean(AssistantPermissions.INVENTORY_PERMISSION, e)}
                />
                Inventory
              </label>

              <label>
                <Checkbox
                  value={(form.value.permissions & AssistantPermissions.CREATE_SHIPMENTS_PERMISSION) !== 0}
                  onChange={(e) => setPermissionsFromBoolean(AssistantPermissions.CREATE_SHIPMENTS_PERMISSION, e)}
                />
                Create Shipment
              </label>

              <label>
                <Checkbox
                  value={(form.value.permissions & AssistantPermissions.MANAGE_SHIPMENT_PERMISSION) !== 0}
                  onChange={(e) => setPermissionsFromBoolean(AssistantPermissions.MANAGE_SHIPMENT_PERMISSION, e)}
                />
                Manage Shipment
              </label>
            </Grid>
          </FormField>
        </Flex>
      </ModalBody>
      
      <ModalFooter>
        <Flex
          justify="end"
          gap={2}
        >
          {loading
            ? <LoadingSpinner />
            : <>
              <Button
                type="secondary"
                onClick={() => onClose()}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                disabled={loading || !form.validate({quiet: true})}
                onClick={handleSave}
              >
                {isUpdating ? "Update" : "Invite"}
              </Button>
            </>
          }
        </Flex>
      </ModalFooter>
    </Modal>
  );
}
